import { athletes } from "@/data/siteContent";
import { Instagram } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";

const PLACEHOLDER_ATHLETE_IMAGE =
  "https://images.unsplash.com/photo-1517649763962-0c623066013b?auto=format&fit=crop&w=900&q=80";

const toSentenceCase = (value) => {
  const cleaned = (value || "").replace(/\s+/g, " ").trim();
  if (!cleaned) {
    return "TBD";
  }
  const sentence = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
};

const toTitleCase = (value) => {
  const cleaned = (value || "").replace(/\s+/g, " ").trim();
  if (!cleaned) {
    return "TBD";
  }
  return cleaned
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getAchievementItems = (value) => {
  const cleaned = (value || "").replace(/^"+|"+$/g, "").trim();
  if (!cleaned) {
    return ["TBD"];
  }

  const numberedItems = cleaned
    .split(/\s(?=\d+\))/)
    .map((item) => item.replace(/^\d+\)\s*/, "").replace(/\s+/g, " ").trim())
    .filter(Boolean);

  return numberedItems.length > 0 ? numberedItems : [cleaned.replace(/\s+/g, " ").trim()];
};

export default function AthletesPage() {
  return (
    <div className="mx-auto w-full max-w-7xl space-y-10 pb-16 sm:space-y-14" data-testid="athletes-page">
      <Reveal>
        <section className="space-y-4" data-testid="athletes-header-section">
          <h1 className="font-oswald text-4xl uppercase tracking-[0.08em] text-accent sm:text-5xl" data-testid="athletes-kicker">
            Represented Athletes
          </h1>
          <p className="max-w-3xl text-sm text-blue-100/88 sm:text-base" data-testid="athletes-intro-text">
            We curate athlete and performance-talent partnerships built for authenticity, trust, and long-term brand impact.
          </p>
        </section>
      </Reveal>

      <section className="grid gap-6 sm:grid-cols-2" data-testid="athletes-grid">
        {athletes.map((athlete, index) => (
          <Reveal key={athlete.name} delay={index * 0.07}>
            <article
              className="overflow-hidden rounded-2xl border border-blue-100/20 bg-card/80 transition-all duration-300 hover:border-accent/70"
              data-testid={`athlete-card-${index + 1}`}
            >
              <div className="relative aspect-[4/3]" data-testid={`athlete-image-wrapper-${index + 1}`}>
                <img
                  src={athlete.image || PLACEHOLDER_ATHLETE_IMAGE}
                  alt={athlete.name || "TBD"}
                  className="h-full w-full object-cover object-center"
                  style={{ objectPosition: athlete.image_position || "50% 20%" }}
                  data-testid={`athlete-image-${index + 1}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#031447]/88 to-transparent" />
                {athlete.instagram_links?.length ? (
                  <div className="absolute right-4 top-4 flex gap-2" data-testid={`athlete-instagram-links-${index + 1}`}>
                    {athlete.instagram_links.map((account, accountIndex) => (
                      <a
                        key={`${athlete.id}-instagram-${accountIndex}`}
                        href={account.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-full border border-white/35 bg-black/35 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-white transition-colors hover:border-accent hover:text-accent"
                        data-testid={`athlete-instagram-link-${index + 1}-${accountIndex + 1}`}
                        aria-label={`${account.label} instagram`}
                      >
                        <Instagram size={12} /> {account.label}
                      </a>
                    ))}
                  </div>
                ) : athlete.instagram_url ? (
                  <a
                    href={athlete.instagram_url}
                    target="_blank"
                    rel="noreferrer"
                    className="absolute right-4 top-4 inline-flex rounded-full border border-white/35 bg-black/35 p-2 text-white transition-colors hover:border-accent hover:text-accent"
                    data-testid={`athlete-instagram-link-${index + 1}`}
                    aria-label={`${athlete.name} instagram`}
                  >
                    <Instagram size={14} />
                  </a>
                ) : null}
                <p
                  className="absolute bottom-4 left-4 rounded-md border border-black/30 bg-black/55 px-3 py-1 font-oswald text-xs uppercase tracking-[0.16em] text-accent shadow-[0_2px_10px_rgba(0,0,0,0.45)] backdrop-blur-sm"
                  style={{ textShadow: "0 1px 2px rgba(0,0,0,0.85)" }}
                  data-testid={`athlete-sport-tag-${index + 1}`}
                >
                  {toTitleCase(athlete.sport)}
                </p>
              </div>

              <div className="space-y-4 p-6">
                <h2 className="font-oswald text-2xl uppercase text-white" data-testid={`athlete-name-${index + 1}`}>
                  {athlete.name || "TBD"}
                </h2>
                <p className="text-sm uppercase tracking-[0.08em] text-blue-100/88" data-testid={`athlete-bio-${index + 1}`}>
                  {toSentenceCase(athlete.bio)}
                </p>
                {(Boolean((athlete.achievements || "").trim()) || !athlete.hide_achievements) && (
                  <ul className="space-y-2" data-testid={`athlete-achievements-${index + 1}`}>
                    {getAchievementItems(athlete.achievements).map((item, achievementIndex) => (
                      <li
                        key={`${athlete.id || index}-${achievementIndex}`}
                        className="text-xs uppercase tracking-[0.14em] text-blue-100/80"
                        data-testid={`athlete-achievement-item-${index + 1}-${achievementIndex + 1}`}
                      >
                        {toSentenceCase(item)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          </Reveal>
        ))}
      </section>
    </div>
  );
}